import AssetPicker from "components/asset-picker/asset-picker";
import Button from "components/button";
import PageTitle from "components/dashboard/page-title";
import Selectable from "components/dashboard/selectable";
import { ROUTES } from "constants/routes";
import dayjs from "dayjs";
import { supabase } from "libs/supabase";
import { LoadingContext } from "providers/loading";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { useId } from "react-id-generator";
import { generatePath, useNavigate } from "react-router-dom";
import { Asset } from "types/asset";
import { Configurator } from "@rromikas/configurio";
import relativeTime from "dayjs/plugin/relativeTime";
import { CONFIGURATOR_CARD_TEST_ID } from "constants/test";
dayjs.extend(relativeTime);

interface ConfiguratorsPageProps {}

const ConfiguratorsPage: FunctionComponent<ConfiguratorsPageProps> = () => {
  const [openPicker, setOpenPicker] = useState(false);
  const [configurators, setConfigurators] = useState<Configurator[]>([]);
  const { setLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    supabase
      .from("configurators")
      .select("*")
      .order("modified_at", { ascending: false })
      .then((res) => {
        setLoading(false);
        if (res.data) {
          setConfigurators(res.data as Configurator[]);
        }
      });
  }, [refreshKey]);

  const ids = useId(configurators.length);

  const onCreate = async (asset: Asset) => {
    setLoading(true);
    const newConfigurator: Configurator = {
      name: "",
      model_url: asset.url,
      model_image_url: asset.image_url,
      options: { customizers: [], groups: [] },
      product_title: "",
      base_price: 0,
    };
    const { data } = await supabase.from("configurators").insert(newConfigurator).select("*");
    setLoading(false);
    if (data?.length) {
      navigate(generatePath(ROUTES.configurator, { id: data[0].id }));
    }
  };

  const deleteSelected = async () => {
    if (window.confirm("Delete selected items?")) {
      setLoading(true);
      await supabase
        .from("configurators")
        .delete()
        .in(
          "id",
          configurators.filter((c, i) => selected.includes(i)).map((c) => c.id as number)
        );
      setLoading(false);
      setRefreshKey((prev) => prev + 1);
      setSelected([]);
    }
  };

  return (
    <div className="p-9">
      {!openPicker ? null : (
        <AssetPicker
          onClose={() => setOpenPicker(false)}
          assetType="model"
          onPick={(asset) => {
            setOpenPicker(false);
            onCreate(asset);
          }}
        ></AssetPicker>
      )}
      <div className="flex items-center justify-between mb-9">
        <PageTitle>Configurators</PageTitle>
        <div className="flex">
          {!selected.length ? null : (
            <Button onClick={deleteSelected} className="mr-4 px-6" variant="secondary-red">
              Delete selected
            </Button>
          )}
          <Button onClick={() => setOpenPicker(true)} className="w-[160px]">
            Create
          </Button>
        </div>
      </div>
      <div className="overflow-hidden">
        <div className="flex flex-wrap -m-2">
          {configurators.map((configurator, i) => (
            <div key={ids[i]} className="p-2">
              <Selectable
                isSelected={selected.includes(i)}
                onSelect={(val) => {
                  if (val) {
                    setSelected((prev) => [...prev, i]);
                  } else {
                    setSelected((prev) => {
                      let arr = [...prev];
                      const index = arr.findIndex((a) => a === i);
                      if (index !== -1) {
                        arr.splice(index, 1);
                      }

                      return arr;
                    });
                  }
                }}
              >
                <div
                  data-testid={CONFIGURATOR_CARD_TEST_ID}
                  onClick={() => navigate(generatePath(ROUTES.configurator, { id: configurator.id }))}
                  className="bg-gray-100 rounded-lg w-[200px] cursor-pointer"
                >
                  <div
                    style={{ backgroundImage: `url(${configurator.model_image_url})` }}
                    className="h-[130px] bg-contain bg-center bg-no-repeat mb-3"
                  ></div>
                  <div className="mb-2 font-bold text-center px-3 leading-tight">{configurator.name}</div>
                  <div className="opacity-50 text-xs text-center pb-3">Edited {dayjs(configurator.modified_at).fromNow()}</div>
                </div>
              </Selectable>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConfiguratorsPage;
